import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, Moon, RefreshCw, Search, Sun } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { visibleItems } from "./navigation";

/*
 * ⌘K / Ctrl+K palette: type a few letters, hit Enter. Navigation and view
 * commands only — nothing here can place, modify or cancel an order.
 */
export function CommandPalette({ open, onClose, role = "admin" }) {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const commands = useMemo(() => {
    const pages = visibleItems(role).map((item) => ({
      id: `nav:${item.path}`,
      label: item.label,
      hint: item.group,
      description: item.description,
      icon: item.icon,
      run: () => navigate(item.path),
    }));
    return [
      ...pages,
      {
        id: "theme",
        label: isDark ? "Switch to light theme" : "Switch to dark theme",
        hint: "View",
        icon: isDark ? Sun : Moon,
        run: toggleTheme,
      },
      {
        id: "reload",
        label: "Reload data",
        hint: "View",
        icon: RefreshCw,
        run: () => window.location.reload(),
      },
    ];
  }, [navigate, role, isDark, toggleTheme]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands
      .map((cmd) => {
        const label = cmd.label.toLowerCase();
        const score = label.startsWith(q) ? 0 : label.includes(q) ? 1 : (cmd.description || "").toLowerCase().includes(q) ? 2 : -1;
        return { cmd, score };
      })
      .filter((r) => r.score >= 0)
      .sort((a, b) => a.score - b.score)
      .map((r) => r.cmd);
  }, [commands, query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setCursor(0);
    const timer = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => setCursor(0), [query]);

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: "nearest" });
  }, [cursor, results]);

  if (!open) return null;

  const runAt = (index) => {
    const cmd = results[index];
    if (!cmd) return;
    onClose();
    cmd.run();
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setCursor((c) => (c + 1) % Math.max(results.length, 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor((c) => (c - 1 + results.length) % Math.max(results.length, 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      runAt(cursor);
    } else if (event.key === "Escape") {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center p-4 pt-[12vh]" role="dialog" aria-modal="true" aria-label="Command palette">
      <button type="button" aria-label="Close command palette" onClick={onClose} className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm animate-fade-in" />

      <div className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-line-strong bg-surface shadow-pop animate-in zoom-in-95">
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Search className="h-4 w-4 shrink-0 text-low" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search screens and actions…"
            className="h-12 w-full border-0 bg-transparent p-0 text-sm text-hi placeholder:text-faint focus:shadow-none focus:outline-none"
          />
          <kbd className="hidden shrink-0 rounded border border-line-strong bg-surface-3 px-1.5 py-0.5 font-mono text-[10px] text-low sm:block">ESC</kbd>
        </div>

        <div ref={listRef} className="max-h-[52vh] overflow-y-auto p-2">
          {results.length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-low">No match for “{query}”</p>
          )}
          {results.map((cmd, index) => {
            const Icon = cmd.icon;
            const active = index === cursor;
            return (
              <button
                key={cmd.id}
                type="button"
                data-active={active}
                onMouseEnter={() => setCursor(index)}
                onClick={() => runAt(index)}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                  active ? "bg-cyan-500/[.12] text-cyan-300" : "text-mid hover:bg-slate-700/30"
                }`}
              >
                {Icon && <Icon className="h-4 w-4 shrink-0" />}
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">{cmd.label}</span>
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-label text-faint">{cmd.hint}</span>
                {active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-low" />}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-4 border-t border-line bg-surface-2 px-4 py-2 text-[10px] text-faint">
          <span><kbd className="font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono">↵</kbd> open</span>
          <span><kbd className="font-mono">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
